<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

// Helpers
import { formatMissionsToSections, moveModalCommandsMap } from "@/modules/companyNeed/helpers/move";
// Services
import missionService from "@/modules/companyNeed/services/missionService";
import { MoveWorkerCompanyNeedCommands } from "@/modules/companyNeed/types/moveWorker";
import { type MissionList, MissionStatus } from "@/modules/companyNeed/views/details/missions/types";

// Types
import type { MissionCommand, MoveWorker, MoveWorkerCommand } from "@/modules/companyNeed/types/moveWorker";

// Components
import Loader from "@/dsComponents/Loader.vue";
import ProgressCircle from "@/dsComponents/progress/ProgressCircle.vue";
import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";
import QuickActionsSeparator from "@/dsComponents/QuickActions/QuickActionsSeparator.vue";

interface Props {
    workers: MoveWorker[];
    selectedIndex?: number | null;
    companyNeedId: string;
}

const props = withDefaults(defineProps<Props>(), {
    selectedIndex: null,
});

const { t } = useI18n();

const emit = defineEmits<{
    (e: "select", command: MoveWorkerCommand | null, index: number): void;
    (e: "update:itemCount", count: number): void;
    (e: "create-order"): void;
}>();

const loading = ref(false);
const missions = ref<MissionList[]>([]);

const missionSections = computed<MissionCommand[]>(() => {
    return formatMissionsToSections(missions.value);
});

const allVisibleCommands = computed(() => {
    const createNewOrderCommand = moveModalCommandsMap[MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER];
    return [...missionSections.value, createNewOrderCommand];
});

watch(
    () => allVisibleCommands.value.length,
    (count) => emit("update:itemCount", count),
    { immediate: true }
);

defineExpose({
    executeSelected: (index: number) => {
        const command = allVisibleCommands.value[index];
        if (command) {
            if (index === missionSections.value.length) {
                handleSelectCreateNewOrder();
            } else {
                handleSelectMission(command, index);
            }
        }
    },
});

const selectedMissionId = ref<string | null>(null);

const fetchMissions = async () => {
    try {
        loading.value = true;
        missions.value = await missionService.fetchMissions(props.companyNeedId, {
            status: [MissionStatus.TO_PROCESS, MissionStatus.TO_VALIDATE, MissionStatus.VALIDATED],
        });
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const handleSelectMission = (command: MoveWorkerCommand, index: number) => {
    selectedMissionId.value = null;
    emit("select", command, index);
};

const handleSelectCreateNewOrder = () => {
    selectedMissionId.value = null;
    emit("create-order");
};

onBeforeMount(() => {
    fetchMissions();
});
</script>

<template>
    <template v-if="loading">
        <div class="missions-quick-action-item__loading">
            <Loader />
        </div>
    </template>
    <template v-else>
        <QuickActionItem
            v-for="(command, index) in missionSections"
            :key="command.key"
            :selected="props.selectedIndex === index"
            @click="handleSelectMission(command, index)"
        >
            <template #infos>
                <div class="missions-quick-action-item__infos">
                    <i
                        v-if="command.icon"
                        :class="['ti', `ti-${command.icon}`, 'typo-md-regular', 'missions-quick-action-item__infos-icon']"
                        :style="{ color: command.iconColor }"
                    />
                    <span class="missions-quick-action-item__infos-label typo-xs-medium">{{ command.label }}</span>
                </div>
            </template>
            <template #more-infos v-if="command.startDate">
                <div class="missions-quick-action-item__more-infos">
                    <span class="typo-xs-regular">{{ command.startDate }}</span>
                    <i v-if="command.endDate" class="ti ti-arrow-right typo-xm-regular" />
                    <span v-if="command.endDate" class="typo-xs-regular">{{ command.endDate }}</span>
                </div>
            </template>
            <template #first-details v-if="command.progressLabel">
                <div class="missions-quick-action-item__progress-circle">
                    <ProgressCircle
                        :fill="command.fill || 0"
                        color="primary"
                        :labelRight="command.progressLabel"
                        label-color="var(--text-sub-600, #494E61)"
                        :label-font-weight="500"
                        size="xs"
                    />
                </div>
            </template>
        </QuickActionItem>

        <QuickActionsSeparator />

        <QuickActionItem :selected="props.selectedIndex === missionSections.length" @click="handleSelectCreateNewOrder">
            <template #infos>
                <div class="missions-quick-action-item__infos">
                    <i class="ti ti-plus typo-md-regular missions-quick-action-item__infos-icon" />
                    <span class="missions-quick-action-item__infos-label typo-xs-medium">
                        {{ t("modals.companyNeed.moveWorker.createNewMissionFromProfiles", { count: workers.length }, workers.length) }}
                    </span>
                </div>
            </template>
        </QuickActionItem>
    </template>
</template>

<style scoped lang="scss">
.missions-quick-action-item {
    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: var(--spacing-3xl, 24px) 0;
    }

    &__infos {
        display: flex;
        align-items: center;
        gap: var(--spacing-lg, 12px);
        user-select: none;
        min-width: 0;

        &-icon {
            color: var(--text-sub-600, #494e61);
        }

        &-label {
            color: var(--icon-sub-500, #636778);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__more-infos {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs, 4px);
        color: var(--text-sub-600, #494e61);
    }

    &__progress-circle {
        padding: 0 var(--spacing-md, 8px);
    }
}

.search-result-item {
    &:hover {
        .missions-quick-action-item__infos-label,
        .missions-quick-action-item__more-infos {
            color: var(--text-main-900, #00061a);
        }
    }

    &--selected {
        .missions-quick-action-item__infos-label,
        .missions-quick-action-item__more-infos {
            color: var(--text-main-900, #00061a);
        }
    }
}
</style>
